// src/lib/incidents-api.js — LOT Incidents 13 (unification admin + ménage)
//
// Helpers REST pour la table Supabase `incidents` (V2 relationnel),
// devenue la SOURCE DE VÉRITÉ UNIQUE admin + ménage.
//
// 3 STATUTS CANONIQUES uniquement (décision Dimitri) :
//   - en_cours    → EN COURS   (signalement ouvert, en attente de traitement)
//   - a_racheter  → À RACHETER (item à remplacer / racheter)
//   - resolu      → RÉSOLU     (clôturé)
//
// Le KV legacy fbz8-incidents n'est plus lu/écrit côté admin (clé conservée,
// cf. CLAUDE.md — pas d'écriture base prod). normalizeStatus() assure la
// robustesse d'affichage si une ligne legacy traîne (mapping → canonique).
//
// Consommé par :
//   - IncidentsTab.jsx (admin : 3 sous-onglets EN COURS / À RACHETER / RÉSOLU)
//   - MenageTodayView.jsx / MenageIncidentsView.jsx (signalement + transitions ménage)

import { supabase } from "../supabase"

const TABLE = "incidents"

// ── Statuts canoniques ─────────────────────────────────────────────

export const STATUS = {
  EN_COURS: "en_cours",
  A_RACHETER: "a_racheter",
  RESOLU: "resolu",
}

export const STATUSES = [STATUS.EN_COURS, STATUS.A_RACHETER, STATUS.RESOLU]

// Mapping legacy → canonique (robustesse affichage / rétrocompat archives).
// Couvre les anciens statuts DB (PR #153 + Phase 2.E.6/4) ET les libellés
// texte V1 KV au cas où.
const LEGACY_TO_CANONICAL = {
  // déjà canoniques
  en_cours: STATUS.EN_COURS,
  a_racheter: STATUS.A_RACHETER,
  resolu: STATUS.RESOLU,
  // legacy → en_cours
  pending: STATUS.EN_COURS,
  to_verify: STATUS.EN_COURS,
  "À traiter": STATUS.EN_COURS,
  "Récupérable": STATUS.EN_COURS,
  // legacy → a_racheter
  to_buy: STATUS.A_RACHETER,
  not_recoverable: STATUS.A_RACHETER,
  "Non récupérable": STATUS.A_RACHETER,
  // legacy → resolu
  recovered: STATUS.RESOLU,
  resolved: STATUS.RESOLU,
  bought: STATUS.RESOLU,
  verified: STATUS.RESOLU,
  aircover_filed: STATUS.RESOLU,
  rejected: STATUS.RESOLU,
  "Remplacé": STATUS.RESOLU,
}

/**
 * Normalise un statut (canonique ou legacy) vers l'une des 3 valeurs
 * canoniques. Fallback en_cours pour tout statut inconnu (jamais throw).
 * @param {string} status
 * @returns {'en_cours'|'a_racheter'|'resolu'}
 */
export function normalizeStatus(status) {
  return LEGACY_TO_CANONICAL[status] || STATUS.EN_COURS
}

// ── Reads ──────────────────────────────────────────────────────────

export async function listIncidents({ status = null, logementId = null, limit = 100 } = {}) {
  let q = supabase.from(TABLE).select("*").order("reported_at", { ascending: false }).limit(limit)
  if (status) q = q.eq("status", status)
  if (logementId) q = q.eq("logement_id", logementId)
  const { data, error } = await q
  if (error) throw error
  return data || []
}

// LOT Photos ménage 27 — restitution admin LECTURE SEULE dans la fiche résa.
// Lit les incidents rattachés à un menage_run (run_id). Lecture admin déjà
// couverte par la RLS admin_all (pas de nouvel endpoint). Colonnes explicites
// alignées sur MenageIncidentsView pour le rendu carte.
export async function listIncidentsByRun(runId) {
  if (!runId) return []
  const { data, error } = await supabase
    .from(TABLE)
    .select("id, logement_id, reservation_id, run_id, source, voyageur, incident_date, label, description, action_taken, photos, degradations, status, admin_note, reported_at, recovered_at, recovered_by, bought_at, bought_by, bought_date")
    .eq("run_id", runId)
    .order("reported_at", { ascending: false })
  if (error) throw error
  return data || []
}

export async function getIncident(id) {
  const { data, error } = await supabase.from(TABLE).select("*").eq("id", id).single()
  if (error) throw error
  return data
}

// Lot 2 photo (ADR-209) — photos d'un incident avec `url` PRÊT À AFFICHER (signed
// URL pour le bucket privé menage-photos ; legacy public passé tel quel). Via
// l'endpoint serveur incident_photos (miroir menage_run_photos). Best-effort :
// renvoie null sur ÉCHEC de signature (≠ [] « aucune photo ») pour que les
// appelants gardent le repli inc.photos au lieu de masquer des photos
// existantes. Sortie : [{ url, fileName, path }] | null.
export async function fetchIncidentPhotos(incidentId) {
  if (!incidentId) return []
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) return null
    const res = await fetch(`/api/admin-users?action=incident_photos&incident_id=${encodeURIComponent(incidentId)}`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
    if (!res.ok) return null
    const { photos } = await res.json()
    return Array.isArray(photos) ? photos : null
  } catch (e) {
    console.error('fetchIncidentPhotos', e)
    return null
  }
}

// ── Permissions 42 Phase 3 Lot 5 — orchestrateur (pool) + timeline admin ──

/**
 * Lecture des incidents du POOL de l'orchestrateur via l'endpoint serveur
 * `orchestrateur_incidents_pool` (cloisonné pool ∧ logIds côté serveur ;
 * whitelist POSITIVE de colonnes, 0 argent, 0 PII). On NE lit JAMAIS la table
 * `incidents` en SELECT client brut pour le pool (la RLS n'a aucune policy
 * SELECT pool : seul l'endpoint expose une projection sûre).
 * @param {{ status?: string, limit?: number }} [opts]
 * @returns {Promise<Array>} incidents (projection sûre)
 */
export async function fetchOrchestratorPoolIncidents({ status = null, limit = 200, month = null } = {}) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('session requise')
  const params = new URLSearchParams({ action: 'orchestrateur_incidents_pool', limit: String(limit) })
  if (status) params.set('status', status)
  // `month` (YYYY-MM) : les RÉGLÉS de ce mois — l'attente arrive toujours,
  // tous mois confondus (règle 2026-08-21).
  if (month) params.set('month', month)
  const res = await fetch(`/api/beds24-mapping?${params.toString()}`, {
    headers: { Authorization: `Bearer ${session.access_token}` },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  const { incidents } = await res.json()
  return Array.isArray(incidents) ? incidents : []
}

/**
 * AUDIT COMPTES 2026-07 — incidents DÉCLARÉS PAR SOI (agent ménage), via
 * l'endpoint serveur `menage_incidents_own` (service_role, whitelist POSITIVE
 * de colonnes : admin_note / action_taken / cost_eur exclus + sous-champs
 * ARGENT de degradations strippés — verrou ADR-227). Remplace le SELECT client
 * direct sur `incidents` (qui exposait l'argent à l'agent) ; la policy RLS
 * `menage_select_own` est retirée en parallèle (migration 20261020000002).
 * @param {{ limit?: number }} [opts]
 * @returns {Promise<Array>} incidents (projection sûre, self only)
 */
export async function fetchOwnIncidents({ limit = 200 } = {}) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('session requise')
  const params = new URLSearchParams({ action: 'menage_incidents_own', limit: String(limit) })
  const res = await fetch(`/api/beds24-mapping?${params.toString()}`, {
    headers: { Authorization: `Bearer ${session.access_token}` },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  const { incidents } = await res.json()
  return Array.isArray(incidents) ? incidents : []
}

// ADR-228 — valeurs canoniques du statut PAR DÉGRADATION (jugement orchestrateur).
// « À vérifier » = neutre (pas encore jugé), « Récupérable » / « Non récupérable »
// = jugement. Toute autre valeur est rejetée (400) côté endpoint.
export const DEGRADATION_STATUTS = ['À vérifier', 'Récupérable', 'Non récupérable']

/**
 * ADR-228 — DÉRIVE le status canonique d'un incident depuis le statutMenage de
 * SES dégradations. Helper PUR (0 I/O), utilisé serveur (miroir CJS) ET client.
 *
 * Règle : si AU MOINS UNE dégradation est « Non récupérable » → 'a_racheter'
 *   (un rachat est nécessaire) ; sinon → 'resolu' (tout récupérable / à vérifier
 *   = clôturé directement, décision Dimitri 3 « tout récupérable → resolu »).
 *
 * NE PAS réutiliser incPrimaryStatut (mauvaise échelle — il produit « Remplacé »
 * qui masquerait un rachat). On IGNORE `remplaceLe` (champ admin) : une dég « Non
 * récupérable » déjà remplacée reste 'a_racheter' au moment du jugement.
 *
 * @param {Array<Object>} degradations
 * @returns {'a_racheter'|'resolu'}
 */
export function deriveStatusFromDegradations(degradations) {
  const degs = Array.isArray(degradations) ? degradations : []
  // Jugement COMPLET requis avant clôture (décision Dimitri : pas de clôture
  // prématurée sur jugement partiel). Une dég est « jugée » ssi statutMenage ∈
  // {Récupérable, Non récupérable}. Tant qu'une dég reste « À vérifier » (ou vide /
  // legacy non jugée), l'incident RESTE 'en_cours' → l'orchestrateur garde la main.
  const JUDGED = new Set(['Récupérable', 'Non récupérable'])
  if (degs.length === 0 || degs.some(d => !d || !JUDGED.has(d.statutMenage))) {
    return STATUS.EN_COURS
  }
  // Toutes jugées : ≥1 « Non récupérable » → rachat, sinon résolu. Ignore remplaceLe.
  return degs.some(d => d.statutMenage === 'Non récupérable') ? STATUS.A_RACHETER : STATUS.RESOLU
}

/**
 * ADR-228 — Qualification PAR DÉGRADATION d'un incident par l'orchestrateur. Le
 * status global est DÉRIVÉ SERVEUR (deriveStatusFromDegradations) ; l'UI envoie
 * uniquement le map { [degId]: statutMenage }. Passe par l'ENDPOINT serveur
 * `orchestrateur_qualify_degradations` (service_role), PAS un UPDATE RLS-direct :
 * l'orchestrateur n'a aucune policy SELECT sur le pool (volontaire — fermer la
 * lecture de l'argent), or PostgreSQL exige la visibilité SELECT pour localiser
 * les lignes d'un UPDATE → un UPDATE RLS-direct trouverait 0 ligne. L'endpoint
 * re-vérifie le scope (pool ∧ logIds FRAIS, OU soi-même), recharge les dégradations
 * du serveur et n'applique QUE `statutMenage` (jamais l'argent : cout/aircover/…
 * viennent du serveur).
 * @param {string} incidentId
 * @param {Object<string,string>} statuts - map { [degId]: "À vérifier"|"Récupérable"|"Non récupérable" }
 * @returns {Promise<Object>} { ok, status }
 */
export async function qualifyDegradations(incidentId, statuts) {
  if (!incidentId) throw new Error('qualifyDegradations: incidentId requis')
  if (!statuts || typeof statuts !== 'object' || Array.isArray(statuts) || Object.keys(statuts).length === 0) {
    throw new Error('qualifyDegradations: statuts requis (map non vide)')
  }
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('session requise')
  const res = await fetch('/api/beds24-mapping', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      action: 'orchestrateur_qualify_degradations',
      incident_id: incidentId,
      statuts,
    }),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    const err = new Error(body.error || 'Action refusée (incident hors de votre périmètre).')
    // Le CODE voyage avec l'erreur : l'écran doit pouvoir distinguer « une
    // dégradation n'existe plus » (il recharge, lot 3 des repères) d'un refus
    // de périmètre — sans relire la phrase du serveur.
    err.code = typeof body.code === 'string' ? body.code : null
    throw err
  }
  return await res.json()
}

/**
 * RACHAT / DÉPÔT par l'orchestrateur (refonte 2026-08-14) — deux dates, jamais
 * un montant. `geste` : 'rachat' (racheteLe) | 'depot' (remplaceLe — l'écran
 * dit « déposé le »). L'identité de trace est posée par le SERVEUR.
 */
export async function rachatDepotOrchestrateur(incidentId, degId, geste, date) {
  if (!incidentId) throw new Error('rachatDepotOrchestrateur: incidentId requis')
  if (!degId) throw new Error('rachatDepotOrchestrateur: degId requis')
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('session requise')
  const res = await fetch('/api/beds24-mapping', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      action: 'orchestrateur_incident_rachat_depot',
      incident_id: incidentId,
      deg_id: degId,
      geste,
      date,
    }),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.error || 'Geste refusé (incident hors de votre périmètre).')
  }
  return await res.json()
}

/**
 * Photos SIGNÉES d'UN incident du pool, pour l'orchestrateur (endpoint LAZY
 * `orchestrateur_incident_photos`, cloisonné pool ∧ logIds FRAIS côté serveur,
 * 0 argent par construction). Best-effort : renvoie [] sur HTTP non-OK ou
 * exception (l'UI n'ouvre rien). Sortie : [{ signedUrl, fileName }] — clé
 * `signedUrl` (consommée par ProprioPhotoViewer), jamais `url`.
 * @param {string} incidentId
 * @returns {Promise<Array<{signedUrl: string, fileName: string|null}>>}
 */
export async function signOrchestratorIncidentPhotos(incidentId) {
  if (!incidentId) return []
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) return []
    const res = await fetch(
      `/api/beds24-mapping?action=orchestrateur_incident_photos&incident_id=${encodeURIComponent(incidentId)}`,
      { headers: { Authorization: `Bearer ${session.access_token}` } },
    )
    if (!res.ok) return []
    const { photos } = await res.json()
    return Array.isArray(photos) ? photos : []
  } catch (_) {
    return []
  }
}

/**
 * Timeline admin d'un incident (dérivée d'audit_log côté serveur, status only,
 * 0 PII voyageur). ADMIN ONLY (endpoint requireAdmin). Best-effort : renvoie []
 * sur échec (l'UI masque la section). Sortie chronologique (création en tête).
 * @param {string} incidentId
 * @returns {Promise<Array<{user_email, user_role, action, old_status, new_status, created_at}>>}
 */
export async function fetchIncidentTimeline(incidentId) {
  if (!incidentId) return []
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) return []
    const res = await fetch(`/api/beds24-mapping?action=incident_timeline&incident_id=${encodeURIComponent(incidentId)}`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
    if (!res.ok) return []
    const { timeline } = await res.json()
    return Array.isArray(timeline) ? timeline : []
  } catch (_) {
    return []
  }
}

/**
 * Dérive le badge « requalifié par {orchestrateur} » depuis une timeline :
 * dernière entrée dont user_role === 'orchestrateur' (changement de statut).
 * @param {Array} timeline (sortie fetchIncidentTimeline)
 * @returns {{ by: string, at: string }|null}
 */
export function lastOrchestratorRequalif(timeline) {
  if (!Array.isArray(timeline)) return null
  let last = null
  for (const e of timeline) {
    if (e.user_role === 'orchestrateur' && e.action === 'UPDATE' && e.old_status !== e.new_status) {
      last = e
    }
  }
  return last ? { by: last.user_email || 'orchestrateur', at: last.created_at } : null
}

// ── Writes — création (admin manuel + run ménage) ──────────────────

/**
 * Crée un incident. Statut initial = en_cours (LOT Incidents 13).
 *
 * Côté ménage (source='run') : RLS menage_insert_own valide reported_by /
 *   source='run' / run_id legitimacy / logement_id JWT + trigger photos_min.
 * Côté admin (source='admin_manual') : INSERT client direct sur la table
 *   `incidents` (PAS un endpoint requireAdmin) — la garantie est la RLS
 *   admin_all (Pattern B) qui autorise l'INSERT au seul rôle admin.
 *
 * @param {Object} params
 * @param {string} params.logement_id - obligatoire
 * @param {string} [params.run_id]
 * @param {'run'|'admin_manual'} [params.source='run']
 * @param {Array} [params.degradations]
 * @param {string} [params.voyageur]
 * @param {string} [params.incident_date]
 * @param {Array} [params.photos]
 * @param {string} [params.notes]
 * @param {string} [params.reported_by]
 * @param {string} [params.label]
 * @param {string} [params.description]
 * @param {string} [params.status='en_cours']
 * @returns {Promise<Object>} incident inséré
 */
export async function createIncident({
  logement_id,
  run_id = null,
  source = 'run',
  degradations = [],
  voyageur = null,
  incident_date = null,
  photos = [],
  // Refonte 2026-08-13 : casse INTERNE (« aucun voyageur en cause ») — l'équipe
  // a cassé. Le proprio ne verra jamais la ligne (filtre serveur).
  casse_interne = false,
  notes = null,
  reported_by = null,
  label = null,
  description = null,
  status = STATUS.EN_COURS,
} = {}) {
  // assertion logement_id (typo silencieuse possible si camelCase legacy)
  if (!logement_id || typeof logement_id !== 'string') {
    throw new Error('createIncident: logement_id requis (string)')
  }
  // Garde-fou B3 (défense applicative en amont du trigger DB) : un incident DOIT
  // comporter au moins une dégradation. Refuser ici évite un INSERT que le trigger
  // BEFORE INSERT rejetterait de toute façon (échec propre, jamais d'incident vide).
  if (!Array.isArray(degradations) || degradations.length === 0) {
    throw new Error('createIncident: au moins une dégradation requise')
  }
  if (!['run', 'admin_manual'].includes(source)) {
    throw new Error(`createIncident: source invalide '${source}' (run|admin_manual)`)
  }
  const payload = {
    logement_id,
    run_id,
    source,
    degradations,
    voyageur,
    casse_interne: !!casse_interne,
    incident_date,
    photos,
    description: description || notes,
    label,
    reported_by,
    status,
  }
  // AUDIT COMPTES 2026-07 : INSERT en retour MINIMAL (plus de .select().single()).
  // Le RETURNING appliquait la policy SELECT sur la ligne insérée ; or la policy
  // `menage_select_own` (lecture large agent) est retirée (verrou argent ADR-227,
  // migration 20261020000002) — la lecture agent passe par l'endpoint whitelist
  // `menage_incidents_own`. Aucun appelant ne consommait la ligne retournée
  // (MenageTodayView / MenageSuiviView / MenageDayDetail / IncidentsTab).
  const { error } = await supabase.from(TABLE).insert(payload)
  if (error) throw error
  return payload
}

// ── Writes — transitions ───────────────────────────────────────────

async function patchIncident(id, patch) {
  const { data, error } = await supabase
    .from(TABLE)
    .update(patch)
    .eq("id", id)
    .select()
    .single()
  if (error) throw error
  return data
}

// REFONTE Lot 5 — markRecovered / markNotRecovered SUPPRIMÉS : l'AGENT ne juge
//   plus (déclaration seule). ADR-228 : le jugement PAR DÉGRADATION (statutMenage)
//   passe par l'ORCHESTRATEUR (qualifyDegradations, endpoint serveur, status DÉRIVÉ
//   serveur) ou par l'ADMIN (markResolved / markToBuy / markBought ci-dessous +
//   rachat-auto IncidentsTab). recovered_at/recovered_by restent LUS en affichage
//   (lignes legacy) mais ne sont plus écrits par aucun parcours.

/**
 * Transition a_racheter → resolu (admin marque rachat effectif).
 * Admin only (RLS bloque ménage côté DB).
 *
 * @param {string} incidentId
 * @param {Object} [opts]
 * @param {string} [opts.bought_date] - date du rachat (défaut today)
 * @param {string} [opts.bought_by] - email admin auteur
 * @returns {Promise<Object>} incident updated
 */
export async function markBought(incidentId, { bought_date = null, bought_by = null } = {}) {
  if (!incidentId) throw new Error('markBought: incidentId requis')
  const today = new Date().toISOString().split('T')[0]
  const { data, error } = await supabase
    .from(TABLE)
    .update({
      status: STATUS.RESOLU,
      bought_at: new Date().toISOString(),
      bought_by,
      bought_date: bought_date || today,
    })
    .eq('id', incidentId)
    .select()
    .single()
  if (error) throw error
  return data
}

/**
 * Transition admin générique vers resolu (clôture manuelle).
 * Utilisé par l'admin pour résoudre un incident sans rachat.
 *
 * @param {string} id
 * @param {Object} [opts]
 * @param {string} [opts.admin_note]
 * @returns {Promise<Object>} incident updated
 */
export function markResolved(id, { admin_note = null } = {}) {
  const patch = { status: STATUS.RESOLU }
  if (admin_note != null) patch.admin_note = admin_note
  return patchIncident(id, patch)
}

/**
 * Transition admin générique vers a_racheter.
 *
 * @param {string} id
 * @returns {Promise<Object>} incident updated
 */
export function markToBuy(id) {
  return patchIncident(id, { status: STATUS.A_RACHETER })
}

/**
 * REFONTE Lot 5 — RACHAT AUTO (bonus admin, en plus de « Marquer racheté »).
 * Moteur PUR (0 I/O) : décide si l'édition admin d'un incident « à racheter »
 * doit le résoudre automatiquement. Règle : l'incident bascule en « résolu »
 * (bought_date = max des dates « À remplacer ») si — et seulement si —
 *   (1) le statut COURANT est 'a_racheter' (un en_cours aux dates remplies ne
 *       saute PAS) ;
 *   (2) il existe AU MOINS UNE dégradation « Non récupérable » (garde anti
 *       [].every() === true) ;
 *   (3) TOUTES les dégradations « Non récupérable » ont leur date `remplaceLe`
 *       remplie (truthy).
 * Retourne null si aucune des conditions n'est remplie (pas de bascule).
 * Ne touche jamais un incident déjà 'resolu' (oldStatus ≠ 'a_racheter' → null).
 *
 * @param {Object} args
 * @param {string|null} args.oldStatus - statut courant (normalisé) de l'incident
 * @param {Array<Object>} args.degradations - dégradations éditées (form)
 * @param {string} [args.adminEmail] - email admin (bought_by)
 * @returns {null | { status, bought_date, bought_at, bought_by }}
 */
export function computeRachatAutoPatch({ oldStatus, degradations, adminEmail = "admin" }) {
  if (oldStatus !== STATUS.A_RACHETER) return null
  const degs = Array.isArray(degradations) ? degradations : []
  const nonRecup = degs.filter(d => d && d.statutMenage === "Non récupérable")
  if (nonRecup.length === 0) return null
  if (!nonRecup.every(d => !!d.remplaceLe)) return null
  const boughtDate = nonRecup
    .map(d => d.remplaceLe)
    .filter(Boolean)
    .sort()
    .pop()
  return {
    status: STATUS.RESOLU,
    bought_date: boughtDate,
    bought_at: new Date().toISOString(),
    bought_by: adminEmail || "admin",
  }
}

export async function deleteIncident(id) {
  const { error } = await supabase.from(TABLE).delete().eq("id", id)
  if (error) throw error
}

// Cascade de suppression d'un logement : purge les incidents V2 rattachés à
// un (ou plusieurs) logement(s) supprimé(s), alignée sur la purge KV
// (fbz8-data / factlog). Sans elle, les incidents restent orphelins
// (logement_id pointant un log inexistant → propId null côté facture/dashboard).
// Gardé par la RLS admin_all (seul l'admin supprime un logement).
// logementIds = string | string[] requis (non vide).
export async function deleteIncidentsByLogement(logementIds) {
  const ids = (Array.isArray(logementIds) ? logementIds : [logementIds])
    .filter(id => typeof id === "string" && id)
  if (ids.length === 0) {
    throw new Error("deleteIncidentsByLogement: logementIds requis (string|string[] non vide)")
  }
  const { error } = await supabase.from(TABLE).delete().in("logement_id", ids)
  if (error) throw error
}

// ── Helpers UI ─────────────────────────────────────────────────────

export const STATUS_META = {
  en_cours:   { label: "En cours",   icon: "📋", color: "#ca8a04" },
  a_racheter: { label: "À racheter", icon: "🛒", color: "#e11d48" },
  resolu:     { label: "Résolu",     icon: "✅", color: "#2d7a3a" },
}

/**
 * Métadonnées d'affichage pour un statut (canonique ou legacy).
 * Normalise d'abord vers canonique → jamais de statut inconnu affiché brut.
 */
export function statusMeta(status) {
  return STATUS_META[normalizeStatus(status)]
}

// ── « QUI PAIE CET INCIDENT ? » (accueil, spec Dimitri 2026-08-26) ──────────

/**
 * Écrit la décision sur UNE dégradation (par id, repli index). Lecture-fusion-
 * écriture du jsonb — le chemin qu'emprunte déjà l'écran Incidents admin ; la
 * RLS admin_all arbitre. Rowcount-check : 0 ligne = refus, jamais silencieux.
 */
export async function trancherQuiPaie(incidentId, degId, patch) {
  if (!incidentId || !patch) throw new Error('trancherQuiPaie: incident et patch requis')
  const { data: lus, error: lErr } = await supabase
    .from(TABLE).select('id, degradations').eq('id', incidentId).limit(1)
  if (lErr) throw lErr
  const inc = lus && lus[0]
  if (!inc) throw new Error('Incident introuvable')
  const degs = Array.isArray(inc.degradations) ? inc.degradations : []
  let trouve = false
  const suivantes = degs.map((d, i) => {
    const cible = (d && d.id && d.id === degId) || (!d.id && String(i) === String(degId))
    if (!cible) return d
    trouve = true
    return { ...d, ...patch }
  })
  if (!trouve) throw new Error('Article introuvable sur cet incident')
  const { data, error } = await supabase
    .from(TABLE)
    .update({ degradations: suivantes })
    .eq('id', incidentId)
    .select('id')
  if (error) throw error
  if (!data || data.length === 0) throw new Error('Décision refusée')
  return true
}

/**
 * Le prix connu d'un article (table linge_prix) — FAIL-SOFT : table absente ou
 * lecture refusée → null, le champ reste vide et la feuille le dit. Jamais un
 * échec de catalogue ne bloque une décision.
 */
export async function lirePrixArticle(article) {
  if (!article) return null
  try {
    // .limit(1) + data[0] plutôt que maybeSingle : même résultat, sans
    // l'en-tête « objet unique » PostgREST (qui casse les harnais mockés).
    const { data, error } = await supabase
      .from('linge_prix').select('prix_eur').eq('article', article).limit(1)
    if (error || !data || !data[0]) return null
    const v = parseFloat(data[0].prix_eur)
    return Number.isFinite(v) && v > 0 ? v : null
  } catch (_) { return null }
}

/** Première saisie → le prix « servira les prochaines fois ». Best-effort. */
export async function retenirPrixArticle(article, prixEur, auteur) {
  if (!article || !Number.isFinite(prixEur) || prixEur <= 0) return false
  try {
    const { error } = await supabase.from('linge_prix')
      .upsert({ article, prix_eur: prixEur, maj_le: new Date().toISOString(), maj_par: auteur || null })
    if (error) { console.error('[accueil] linge_prix', error.message); return false }
    return true
  } catch (e) { console.error('[accueil] linge_prix', e?.message || e); return false }
}
